import { AlertTriangle, Circle, CircleCheck, CircleX, Search, Send, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AttributeGroupCard,
  enrichmentRunPanelReducer,
  INITIAL_PANEL_STATE,
  isGroupLocked,
  labelForKey as resolveLabel,
  selectedAttributeKeys,
} from '~/components/common/enrichment-run-panel';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/utils/cn';

import type { DeliveryDecision, DeliverySelection } from './types';
import { useDeliveryAttributeGroups } from './use-delivery-attribute-groups';

interface DeliveryRunPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Attribute keys this run enriched — the picker is narrowed to these. `undefined`
   * means the run's set isn't known here, so the full taxonomy is shown instead of
   * an empty picker (see `use-delivery-attribute-groups`).
   */
  eligibleKeys?: string[];
  /** Whether the operator may reject at this surface. Approve-only when false. */
  allowReject?: boolean;
  /** Request in flight — disables the actions and shows the pending state. */
  isSubmitting: boolean;
  onSubmit: (selection: DeliverySelection) => void;
  /** Optional line under the title, e.g. a batch name or SKU count. */
  subtitle?: string;
  /** Which platform's attribute source to show. Both platforms offer every enriched
   *  field as a deselectable option. Defaults to Google. */
  platform?: 'google' | 'meta';
  /** This run's `alwaysOnAttributes` (job-status echo) — agent-produced fields kept
   *  in the picker even when they weren't part of the requested set. */
  alwaysOnKeys?: string[];
  /** Extra decision content shown above the note while approving (e.g. the language gate).
   *  The caller owns its state and folds it into the approval payload itself. */
  extraSection?: ReactNode;
  /** `data-testid` for the sheet; every control derives from it (`-approve-option`, `-submit-button`, ...). */
  testId?: string;
}

/**
 * Right-anchored side panel for actioning a batch paused at AWAITING_APPROVAL:
 * the approve/reject decision, an optional delivery note, and — on approval — a
 * picker for which of the run's enriched attributes to push.
 *
 * The attribute picker, its dependency locks, and the search box are the exact
 * building blocks the creation `EnrichmentRunPanel` uses (shared reducer +
 * `AttributeGroupCard`), so delivery and creation stay visually and behaviourally
 * in step. Built on `Sheet` (Radix Dialog) for the focus trap, Esc/scrim
 * dismissal, and focus return.
 *
 * Content is remounted per open (Radix unmounts `SheetContent` when closed), so
 * every open starts from the default "deliver everything enriched" state.
 */
export function DeliveryRunPanel({
  open,
  onOpenChange,
  eligibleKeys,
  allowReject = true,
  isSubmitting,
  onSubmit,
  subtitle,
  platform = 'google',
  alwaysOnKeys,
  extraSection,
  testId = 'delivery-run-panel',
}: DeliveryRunPanelProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Hold the panel open while the decision is being submitted so it cannot
        // be dismissed into an unknown state mid-request.
        if (!next && isSubmitting) return;
        onOpenChange(next);
      }}
    >
      <SheetContent
        side="right"
        data-testid={testId}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isSubmitting) e.preventDefault();
        }}
      >
        <DeliveryRunPanelContent
          eligibleKeys={eligibleKeys}
          platform={platform}
          allowReject={allowReject}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          testId={testId}
          onCancel={() => {
            onOpenChange(false);
          }}
          {...(subtitle !== undefined ? { subtitle } : {})}
          {...(alwaysOnKeys ? { alwaysOnKeys } : {})}
          {...(extraSection ? { extraSection } : {})}
        />
      </SheetContent>
    </Sheet>
  );
}

interface ContentProps {
  /** Explicitly nullable rather than optional — the parent always forwards the
   *  prop's value, which is `undefined` when no run set is known. */
  eligibleKeys: string[] | undefined;
  platform: 'google' | 'meta';
  allowReject: boolean;
  isSubmitting: boolean;
  onSubmit: (selection: DeliverySelection) => void;
  onCancel: () => void;
  subtitle?: string;
  alwaysOnKeys?: string[];
  extraSection?: ReactNode;
  testId: string;
}

/** Panel body. Lives inside `SheetContent` so its state resets on every open. */
function DeliveryRunPanelContent({
  eligibleKeys,
  platform,
  allowReject,
  isSubmitting,
  onSubmit,
  onCancel,
  subtitle,
  alwaysOnKeys,
  extraSection,
  testId,
}: ContentProps) {
  const { t } = useTranslation('common');
  const { groups, isLoading, isError, refetch } = useDeliveryAttributeGroups(
    true,
    eligibleKeys,
    platform,
    alwaysOnKeys,
  );
  const [state, dispatch] = useReducer(enrichmentRunPanelReducer, INITIAL_PANEL_STATE, (init) => {
    // Seed selection directly on mount rather than via a HYDRATE effect — the
    // groups are already resolved by the time the body renders when the taxonomy
    // is cached, and a synchronous seed avoids a first paint with nothing on.
    return groups.length > 0 ? enrichmentRunPanelReducer(init, { type: 'HYDRATE', groups }) : init;
  });
  const [decision, setDecision] = useState<DeliveryDecision>('APPROVED');
  const [notes, setNotes] = useState('');
  const [isReviewOpen, setReviewOpen] = useState(false);

  // Re-seed selection when the taxonomy resolves after first paint (cold cache)
  // or when `eligibleKeys` narrows the set. HYDRATE no-ops on an unchanged group
  // set, so a background refetch can't wipe a live selection.
  useEffect(() => {
    if (groups.length > 0) dispatch({ type: 'HYDRATE', groups });
  }, [groups]);

  const query = state.search.trim().toLowerCase();
  const isSearching = query.length > 0;
  const visibleGroups = groups
    .map((group) => ({
      group,
      visibleAttributes: isSearching
        ? group.attributes.filter((a) => a.label.toLowerCase().includes(query))
        : group.attributes,
    }))
    .filter(({ visibleAttributes }) => visibleAttributes.length > 0);

  const selectedKeys = selectedAttributeKeys(state);
  const availableKeys = groups.flatMap((g) => g.attributes.map((a) => a.key));
  // Select all is spent once every group is fully chosen — a single-choice group
  // counts as satisfied with one option picked, since it can never be "all on",
  // and an unavailable group is skipped entirely because the cascade forces it
  // off (counting it would leave Select all permanently enabled and inert).
  const selectableGroups = groups.filter((g) => g.unavailable !== true);
  const allSelected =
    selectableGroups.length > 0 &&
    selectableGroups.every((g) =>
      g.singleSelect
        ? g.attributes.some((a) => state.selected[a.key])
        : g.attributes.every((a) => state.selected[a.key]),
    );
  const isApprove = decision === 'APPROVED';
  const hasAttributes = groups.length > 0;
  // Require at least one attribute only when the picker actually offered some —
  // a taxonomy that failed to load must not block the delivery decision itself,
  // in which case no attribute slice is sent and the server delivers all.
  const approveReady = !hasAttributes || selectedKeys.length > 0;
  const canSubmit = !isSubmitting && (isApprove ? approveReady : true);

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      decision,
      notes: notes.trim(),
      attributeKeys: isApprove ? selectedKeys : [],
      availableKeys: isApprove ? availableKeys : [],
    });
  };

  return (
    <>
      <header className="shrink-0 border-b px-6 pt-5 pb-4">
        <SheetTitle data-testid={`${testId}-title`} className="text-lg leading-8 font-semibold">
          {t('deliveryRun.title')}
        </SheetTitle>
        <SheetDescription data-testid={`${testId}-subtitle`} className="mt-0.5 text-sm">
          {subtitle ?? t('deliveryRun.subtitle')}
        </SheetDescription>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Decision — hidden on approve-only surfaces where reject isn't offered. */}
        {allowReject && (
          <section data-testid={`${testId}-decision`} className="space-y-2 border-b px-6 py-5">
            <Label className="text-sm font-semibold">{t('deliveryRun.decision.label')}</Label>
            <RadioGroup
              data-testid={`${testId}-decision-radio-group`}
              value={decision}
              disabled={isSubmitting}
              onValueChange={(value) => {
                setDecision(value as DeliveryDecision);
              }}
              className="grid grid-cols-2 gap-3"
            >
              <Label
                htmlFor="delivery-decision-approve"
                data-testid={`${testId}-approve-option`}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium',
                  isApprove ? 'border-primary bg-primary/5' : 'border-input',
                )}
              >
                {/* Radio kept for a11y/keyboard but visually hidden — the check
                    icon is the affordance. */}
                <RadioGroupItem
                  value="APPROVED"
                  id="delivery-decision-approve"
                  data-testid={`${testId}-approve-radio`}
                  className="sr-only"
                />
                {isApprove ? (
                  <CircleCheck aria-hidden="true" className="text-primary size-5 shrink-0" />
                ) : (
                  <Circle aria-hidden="true" className="text-primary size-5 shrink-0" />
                )}
                {t('deliveryRun.decision.approve')}
              </Label>
              <Label
                htmlFor="delivery-decision-reject"
                data-testid={`${testId}-reject-option`}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium',
                  !isApprove ? 'border-destructive bg-destructive/5' : 'border-input',
                )}
              >
                <RadioGroupItem
                  value="REJECTED"
                  id="delivery-decision-reject"
                  data-testid={`${testId}-reject-radio`}
                  className="sr-only"
                />
                {!isApprove ? (
                  <CircleX aria-hidden="true" className="text-destructive size-5 shrink-0" />
                ) : (
                  <Circle aria-hidden="true" className="text-primary size-5 shrink-0" />
                )}
                {t('deliveryRun.decision.reject')}
              </Label>
            </RadioGroup>
          </section>
        )}

        {/* Attributes — only meaningful for a delivery; hidden on rejection. */}
        {isApprove && (
          <section data-testid={`${testId}-attributes`} className="space-y-3.5 border-b px-6 py-5">
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-foreground text-sm font-semibold">
                  {t('deliveryRun.attributes.title')}
                </h3>
                <div className="flex items-center gap-3">
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isSubmitting || isLoading || allSelected}
                    data-testid={`${testId}-select-all-button`}
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'SELECT_ALL' });
                    }}
                  >
                    {t('deliveryRun.attributes.selectAll')}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isSubmitting || isLoading || selectedKeys.length === 0}
                    data-testid={`${testId}-clear-all-button`}
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'CLEAR_ALL' });
                    }}
                  >
                    {t('deliveryRun.attributes.clearAll')}
                  </Button>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                {t('deliveryRun.attributes.description')}
              </p>
            </div>

            {isError ? (
              <div
                data-testid={`${testId}-attributes-error`}
                className="space-y-2.5 rounded-lg border px-4 py-4 text-center"
              >
                <p className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
                  <AlertTriangle className="text-warning size-4 shrink-0" aria-hidden="true" />
                  {t('deliveryRun.attributes.error')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  data-testid={`${testId}-attributes-retry-button`}
                  onClick={refetch}
                >
                  {t('deliveryRun.attributes.retry')}
                </Button>
              </div>
            ) : isLoading ? (
              <div
                data-testid={`${testId}-attributes-loading`}
                className="space-y-2.5"
                aria-busy="true"
              >
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : groups.length === 0 ? (
              <p
                data-testid={`${testId}-attributes-empty`}
                className="text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm"
              >
                {t('deliveryRun.attributes.empty')}
              </p>
            ) : (
              <>
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                  />
                  <Input
                    value={state.search}
                    data-testid={`${testId}-attribute-search-input`}
                    placeholder={t('deliveryRun.attributes.searchPlaceholder')}
                    className="pr-9 pl-9"
                    onChange={(e) => {
                      dispatch({ type: 'SET_SEARCH', search: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && state.search) {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }
                    }}
                  />
                  {state.search && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('deliveryRun.attributes.clearSearch')}
                      data-testid={`${testId}-attribute-search-clear-button`}
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-7 w-7 -translate-y-1/2 cursor-pointer"
                      onClick={() => {
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }}
                    >
                      <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                {visibleGroups.length === 0 ? (
                  <p
                    data-testid={`${testId}-attributes-no-results`}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    {t('deliveryRun.attributes.noResults', { query: state.search })}
                  </p>
                ) : (
                  <div className="space-y-2.5">
                    {visibleGroups.map(({ group, visibleAttributes }) => {
                      const locked = isGroupLocked(state, group);
                      return (
                        <AttributeGroupCard
                          key={group.slug}
                          testId={`${testId}-group-${group.slug}`}
                          group={group}
                          visibleAttributes={visibleAttributes}
                          selected={state.selected}
                          locked={locked}
                          requiresLabel={
                            group.requiresKey === undefined
                              ? null
                              : resolveLabel(groups, group.requiresKey)
                          }
                          expanded={isSearching || state.expanded[group.slug] !== false}
                          labelForKey={(key) => resolveLabel(groups, key)}
                          onToggleGroup={() => {
                            dispatch({ type: 'TOGGLE_GROUP', slug: group.slug });
                          }}
                          onToggleExpand={() => {
                            dispatch({ type: 'TOGGLE_EXPAND', slug: group.slug });
                          }}
                          onToggleAttribute={(key) => {
                            dispatch({ type: 'TOGGLE_ATTRIBUTE', key });
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {isApprove ? extraSection : null}

        {/* Note — a delivery note on approval, the rejection reason on reject.
            Optional in both cases. */}
        <section className="space-y-1.5 px-6 py-5">
          <Label htmlFor="delivery-notes" className="text-sm font-semibold">
            {isApprove ? t('deliveryRun.notes.label') : t('deliveryRun.notes.rejectLabel')}
          </Label>
          <Textarea
            id="delivery-notes"
            data-testid={`${testId}-notes-input`}
            placeholder={
              isApprove
                ? t('deliveryRun.notes.placeholder')
                : t('deliveryRun.notes.rejectPlaceholder')
            }
            value={notes}
            disabled={isSubmitting}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
          />
        </section>
      </div>

      <footer className="shrink-0 border-t px-6 py-4">
        {isApprove && isReviewOpen && selectedKeys.length > 0 && (
          <div
            data-testid={`${testId}-review-selection`}
            className="bg-muted/40 mb-3 max-h-32 overflow-y-auto rounded-lg border p-2.5"
          >
            <div className="flex flex-wrap gap-1.5">
              {selectedKeys.map((key) => (
                <span
                  key={key}
                  data-testid={`${testId}-review-selection-item-${key}`}
                  className="bg-selected-row text-muted-blue rounded-full px-2 py-0.5 text-xs font-medium"
                >
                  {resolveLabel(groups, key)}
                </span>
              ))}
            </div>
          </div>
        )}
        {isApprove && hasAttributes && (
          <div className="mb-3 flex items-center justify-between gap-3">
            <p data-testid={`${testId}-footer-summary`} className="text-muted-foreground text-sm">
              {t('deliveryRun.footer.summary', { attributes: selectedKeys.length })}
            </p>
            <Button
              variant="link"
              size="sm"
              disabled={selectedKeys.length === 0}
              aria-expanded={isReviewOpen}
              data-testid={`${testId}-review-selection-button`}
              className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
              onClick={() => {
                setReviewOpen((prev) => !prev);
              }}
            >
              {isReviewOpen
                ? t('deliveryRun.footer.hideSelection')
                : t('deliveryRun.footer.reviewSelection')}
            </Button>
          </div>
        )}
        <div className="flex gap-2.5">
          <Button
            variant="outline"
            disabled={isSubmitting}
            className="cursor-pointer disabled:cursor-not-allowed"
            data-testid={`${testId}-cancel-button`}
            onClick={onCancel}
          >
            {t('deliveryRun.footer.cancel')}
          </Button>
          <Button
            variant={isApprove ? 'default' : 'destructive'}
            disabled={!canSubmit}
            isLoading={isSubmitting}
            className="flex-1 cursor-pointer gap-2 disabled:cursor-not-allowed"
            data-testid={`${testId}-submit-button`}
            onClick={submit}
          >
            {isApprove && <Send aria-hidden="true" className="h-4 w-4" />}
            {isApprove ? t('deliveryRun.footer.approve') : t('deliveryRun.footer.reject')}
          </Button>
        </div>
      </footer>
    </>
  );
}
